// ═══════════════════════════════════════════════════════════════
//  interventionsModel.js — logique PURE du module Interventions (I-SOIR)
//
//  ⚠️ N'IMPORTE QUE DES VOISINS SANS IMPORT (extension `.js` obligatoire) :
//  interventionsModel.test.js le charge POUR DE VRAI en `node` pur. Même
//  parti pris que taches/workflow.js et taches/versPlanning.js.
//
//  Contient : énumérations, validation du formulaire, construction du
//  document (§3.1 du ticket — ses clés et rien d'autre : la règle Firestore
//  fait un keys().hasOnly), calcul des accès, transitions (§3.2), prédicats
//  d'écran MIROIRS des règles, états affichés (§3.3), numérotation,
//  notifications d'accueil.
//
//  Réserve ≠ intervention : une réserve est un défaut constaté sur un
//  chantier livré (module Réserves) ; une intervention est un travail
//  commandé par un client, hors chantier, planifié et réalisé.
// ═══════════════════════════════════════════════════════════════
import { ROLES_PLANIFICATEURS, peutPlanifier } from "../taches/versPlanning.js";

export { ROLES_PLANIFICATEURS, peutPlanifier };

// ─── Énumérations ──────────────────────────────────────────────
export const STATUTS = ["a_planifier", "planifiee", "realisee", "facturee", "annulee"];
export const STATUT_LABEL = {
  a_planifier: "À planifier",
  planifiee: "Planifiée",
  realisee: "Réalisée",
  facturee: "Facturée",
  annulee: "Annulée",
};
// Ton de <Badge> par statut (familles existantes, aucune couleur nouvelle).
export const STATUT_TON = {
  a_planifier: "warning",
  planifiee: "info",
  realisee: "success",
  facturee: "neutral",
  annulee: "neutral",
};
export const URGENCES = ["normale", "urgente"];
export const ORIGINES = ["depot", "saisie"];
export const UNITES_TEMPS = ["demi", "heure"];

export const LIMITES = Object.freeze({
  travaux: 200,
  description: 5000,
  numBonCommande: 60,
  texteCourt: 200,          // noms, e-mail
  adresse: 300,             // adresse et précision du lieu
  telephone: 40,
  intervenants: 30,
  detailLignes: 20,
  detailLibelle: 200,
  detailDemiJournees: 40,   // demi-journées par ligne de détail : 1..40
  idLigne: 80,              // id d'une ligne de détail (= interventionLigneId)
  photosRealisation: 5,
  commentaireRealisation: 5000,
  heuresPassees: 1000,
  passages: 100,
  heuresPassage: 24,
  commentairePassage: 2000,
  signataireNom: 200,
  signataireQualite: 100,
  motifAnnulationMin: 3,
  motifAnnulation: 500,
});

// Mode de mise au planning (§3.1) : « bloc » (toute l'intervention d'un
// tenant) ou « lignes » (chaque ligne du détail planifiée à part).
// « lignes » est INTERDIT sans détail (règle + modèle).
export const MODES_PLANIF = ["bloc", "lignes"];
export function modePlanifValide(mode, detail) {
  if (mode === "bloc") return true;
  return mode === "lignes" && Array.isArray(detail) && detail.length > 0;
}

// ─── Rôles (claim `role` = roles[0], comparé à l'identique) ────
// Terrain : lecture limitée à ses fiches, ne peut que réaliser.
export const ROLES_TERRAIN = ["Monteur", "Artisan"];
// Facturation : realisee ↔ facturee.
export const ROLES_FACTURATION = ["Admin", "Direction", "Assistante"];
// Suppression : `allow delete: if isDirection()` (Admin + Direction).
export const ROLES_SUPPRESSION = ["Admin", "Direction"];

// Supervision : voient TOUTES les fiches (requête nue). MIROIR EXACT de la
// fonction `estSuperviseur()` du `match /interventions` de firestore.rules
// (test de dérive dans interventionsModel.test.js).
export const ROLES_SUPERVISION_INTERVENTIONS = ["Admin", "Direction", "Assistante"];

// Le rôle opposable aux règles est le claim, c'est-à-dire roles[0].
// Jamais `user.role`.
export function roleClaim(user) {
  return Array.isArray(user?.roles) && typeof user.roles[0] === "string" ? user.roles[0] : null;
}
export function estTerrain(role) {
  return typeof role === "string" && ROLES_TERRAIN.includes(role);
}
export function estSuperviseur(role) {
  return typeof role === "string" && ROLES_SUPERVISION_INTERVENTIONS.includes(role);
}

// ─── Visibilité (§1.1) ─────────────────────────────────────────
// Ouverte = aucun intervenant ET statut « à planifier » : visible par tout
// employé ayant le module. Attribuée = non ouverte : visible des seuls
// superviseurs, du responsable et des intervenants (accesUids).
// Champ stocké `ouverte`, recalculé par avecAcces, jamais saisi.
export function estOuverte(i) {
  const ids = Array.isArray(i?.intervenantIds) ? i.intervenantIds.filter(Boolean) : [];
  return ids.length === 0 && i?.statut === "a_planifier";
}

// ─── Accès : UN helper unique, appelé à CHAQUE écriture ────────
// accesUids = responsable.uid ∪ intervenantUids (dédupliqués, non vides) —
// c'est sur lui que la règle et la requête terrain s'appuient.
// accesIds = idem en id `utilisateurs` (filtre client own_items).
export function calculerAcces({ responsable, intervenantIds, intervenantUids } = {}) {
  const uids = [], ids = [];
  const ajoute = (liste, v) => {
    const s = typeof v === "string" ? v.trim() : "";
    if (s && !liste.includes(s)) liste.push(s);
  };
  ajoute(uids, responsable?.uid);
  (intervenantUids || []).forEach((u) => ajoute(uids, u));
  ajoute(ids, responsable?.id);
  (intervenantIds || []).forEach((i) => ajoute(ids, i));
  return { accesUids: uids, accesIds: ids };
}

// Renvoie `data` complété des champs d'accès recalculés : accesUids,
// accesIds ET `ouverte`. Helper UNIQUE, appelé à CHAQUE écriture de fiche.
export function avecAcces(data) {
  return { ...data, ...calculerAcces(data), ouverte: estOuverte(data) };
}
// Les trois champs d'accès d'une fiche recalculée (à poser dans un patch).
export function champsAcces(fusion) {
  return { accesUids: fusion.accesUids, accesIds: fusion.accesIds, ouverte: fusion.ouverte };
}
// Seuls les champs d'accès qui CHANGENT par rapport à la fiche en base
// (`ouverte` absent = false, comme la règle). Pour les écritures de la
// branche terrain (passage, clôture), dont la règle fige la liste des clés :
// un champ inchangé n'y est pas réécrit.
export function champsAccesModifies(actuelle, fusion) {
  const out = {};
  const memeListe = (a, b) => JSON.stringify(Array.isArray(a) ? a : []) === JSON.stringify(Array.isArray(b) ? b : []);
  if (!memeListe(actuelle?.accesUids, fusion.accesUids)) out.accesUids = fusion.accesUids;
  if (!memeListe(actuelle?.accesIds, fusion.accesIds)) out.accesIds = fusion.accesIds;
  if ((actuelle?.ouverte === true) !== fusion.ouverte) out.ouverte = fusion.ouverte;
  return out;
}

// Lecture permise (miroir de la règle `read`) : superviseur, fiche ouverte,
// ou personne présente dans accesUids.
export function peutVoirIntervention({ role, uid, intervention }) {
  return estSuperviseur(role) || estOuverte(intervention) || aAcces(intervention, uid);
}

// §1.3 — La personne qui planifie perdra-t-elle la fiche de vue ?
// Après l'écriture, la fiche est attribuée (au moins un intervenant) et
// cette personne n'est ni superviseur, ni responsable, ni intervenant.
// `personnes` = intervenants qui seront retenus (ajoutés à ceux en base).
export function perdraVisibilite({ role, uid, intervention, personnes }) {
  if (!uid || estSuperviseur(role)) return false;
  const apres = { ...(intervention || {}), ...unirIntervenants(intervention, personnes) };
  if (!apres.intervenantIds.length) return false;
  return !calculerAcces(apres).accesUids.includes(uid);
}
export const MESSAGE_PERTE_VISIBILITE = "Une fois attribuée, cette intervention ne sera plus visible pour vous. Ajoutez-vous comme intervenant ou changez de responsable pour la garder.";
export const MESSAGE_FICHE_ATTRIBUEE = "Cette intervention a été attribuée, elle n'est plus visible pour vous.";

// ─── Personnes ─────────────────────────────────────────────────
// Une personne retenue porte TOUJOURS ses deux identifiants : id du doc
// `utilisateurs` (ressourceId du planning) et uid Auth (règles).
export function personneDepuisUtilisateur(u) {
  const id = u?._id || u?.id || "";
  const nom = `${u?.prenom || ""} ${u?.nom || ""}`.trim() || id;
  return { id, uid: u?.uid || "", nom };
}
export function personneValide(p) {
  return !!(p && typeof p.id === "string" && p.id && typeof p.uid === "string" && p.uid);
}

// Les trois tableaux d'intervenants, même longueur, même ordre.
export function intervenantsDe(intervention) {
  const ids = intervention?.intervenantIds || [];
  const uids = intervention?.intervenantUids || [];
  const noms = intervention?.intervenantNoms || [];
  return ids.map((id, i) => ({ id, uid: uids[i] || "", nom: noms[i] || id }));
}
export function champsIntervenants(personnes) {
  const vues = new Set();
  const liste = (personnes || []).filter((p) => personneValide(p) && !vues.has(p.id) && vues.add(p.id))
    .slice(0, LIMITES.intervenants);
  return {
    intervenantIds: liste.map((p) => p.id),
    intervenantUids: liste.map((p) => p.uid),
    intervenantNoms: liste.map((p) => p.nom || p.id),
  };
}
// Ajoute des personnes à celles déjà retenues (sans doublon, ordre conservé).
export function unirIntervenants(intervention, ajouts) {
  return champsIntervenants([...intervenantsDe(intervention), ...(ajouts || [])]);
}

// ─── Temps estimé ──────────────────────────────────────────────
// demi : entier ≥ 1 · heure : > 0, pas de 0,5.
export function normaliserTempsEstime(t) {
  const unite = UNITES_TEMPS.includes(t?.unite) ? t.unite : "demi";
  const v = Number(String(t?.valeur ?? "").replace(",", "."));
  if (unite === "demi") {
    return { unite, valeur: Number.isFinite(v) && v >= 1 ? Math.round(v) : 1 };
  }
  const arrondi = Number.isFinite(v) ? Math.round(v * 2) / 2 : 0;
  return { unite, valeur: arrondi >= 0.5 ? arrondi : 0.5 };
}
export function libelleTempsEstime(t) {
  const n = normaliserTempsEstime(t);
  if (n.unite === "demi") return `${n.valeur} demi-journée${n.valeur > 1 ? "s" : ""}`;
  return `${String(n.valeur).replace(".", ",")} h`;
}

// ─── Formulaire ────────────────────────────────────────────────
export function formulaireVide() {
  return {
    client: { nom: "", contactNom: "", telephone: "", email: "" },
    numBonCommande: "",
    lieu: { adresse: "", precision: "" },
    contactSurPlace: { nom: "", telephone: "" },
    travaux: "",
    description: "",
    detail: [],
    urgence: "normale",
    tempsEstime: { unite: "demi", valeur: 1 },
    responsable: null,
  };
}

// Formulaire pré-rempli depuis un document existant (« Modifier »).
export function formulaireDepuisIntervention(i) {
  const v = formulaireVide();
  return {
    client: { ...v.client, ...pick(i?.client, Object.keys(v.client)) },
    numBonCommande: str(i?.numBonCommande),
    lieu: { ...v.lieu, ...pick(i?.lieu, Object.keys(v.lieu)) },
    contactSurPlace: { ...v.contactSurPlace, ...pick(i?.contactSurPlace, Object.keys(v.contactSurPlace)) },
    travaux: str(i?.travaux),
    description: str(i?.description),
    // L'id de chaque ligne est CONSERVÉ : c'est lui que porte le planning
    // (interventionLigneId) — le régénérer couperait le lien.
    detail: Array.isArray(i?.detail) ? i.detail.map((d) => ({ id: str(d?.id), libelle: str(d?.libelle), demiJournees: d?.demiJournees ?? 1 })) : [],
    urgence: URGENCES.includes(i?.urgence) ? i.urgence : "normale",
    tempsEstime: normaliserTempsEstime(i?.tempsEstime),
    responsable: i?.responsable ? { id: str(i.responsable.id), uid: str(i.responsable.uid), nom: str(i.responsable.nom) } : null,
  };
}

function str(v) { return typeof v === "string" ? v : (v == null ? "" : String(v)); }
function pick(obj, cles) {
  const out = {};
  for (const k of cles) if (obj && obj[k] != null) out[k] = str(obj[k]);
  return out;
}
const coupe = (v, n) => str(v).trim().slice(0, n);

// ─── Détail des travaux : id STABLE par ligne ──────────────────
// Généré côté client à la création de la ligne (formulaire, IA), puis
// conservé à chaque édition : le planning le porte (interventionLigneId).
export function faireIdLigne() {
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}
// Ligne vide de formulaire, avec son id.
export function nouvelleLigneDetail(faireId = faireIdLigne) {
  return { id: faireId(), libelle: "", demiJournees: 1 };
}
// Normalise le détail : libellé ≤ 200, demi-journées entières 1..40, id
// conservé s'il est valide et unique, sinon généré. Lignes sans libellé
// retirées, 20 lignes au plus.
export function normaliserDetail(detail, faireId = faireIdLigne) {
  const vus = new Set();
  const out = [];
  for (const d of Array.isArray(detail) ? detail : []) {
    const libelle = coupe(d?.libelle, LIMITES.detailLibelle);
    if (!libelle) continue;
    let id = str(d?.id).trim().slice(0, LIMITES.idLigne);
    while (!id || vus.has(id)) id = str(faireId()).slice(0, LIMITES.idLigne);
    vus.add(id);
    const n = Math.round(Number(d?.demiJournees) || 1);
    out.push({ id, libelle, demiJournees: Math.min(LIMITES.detailDemiJournees, Math.max(1, n)) });
    if (out.length >= LIMITES.detailLignes) break;
  }
  return out;
}

// Erreurs en ligne : { [champ]: message }. Objet vide = valide.
export function validerFormulaire(f) {
  const e = {};
  if (!str(f?.client?.nom).trim()) e["client.nom"] = "Le client est obligatoire.";
  if (!str(f?.lieu?.adresse).trim()) e["lieu.adresse"] = "L'adresse est obligatoire.";
  const travaux = str(f?.travaux).trim();
  if (!travaux) e.travaux = "Les travaux sont obligatoires.";
  else if (travaux.length > LIMITES.travaux) e.travaux = `${LIMITES.travaux} caractères maximum.`;
  if (str(f?.description).length > LIMITES.description) e.description = `${LIMITES.description} caractères maximum.`;
  if (str(f?.numBonCommande).trim().length > LIMITES.numBonCommande) e.numBonCommande = `${LIMITES.numBonCommande} caractères maximum.`;
  if (!personneValide(f?.responsable)) e.responsable = "Le responsable du suivi est obligatoire.";
  const email = str(f?.client?.email).trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e["client.email"] = "Adresse e-mail invalide.";
  const detail = Array.isArray(f?.detail) ? f.detail : [];
  if (detail.length > LIMITES.detailLignes) e.detail = `${LIMITES.detailLignes} lignes maximum.`;
  else if (detail.some((d) => str(d?.libelle).trim() && !(Number.isInteger(Number(d?.demiJournees))
    && Number(d.demiJournees) >= 1 && Number(d.demiJournees) <= LIMITES.detailDemiJournees))) {
    e.detail = `Chaque ligne compte de 1 à ${LIMITES.detailDemiJournees} demi-journées entières.`;
  }
  return e;
}

// Champs métier du formulaire, nettoyés et bornés — partie commune à la
// création et à la modification. `faireId` : générateur d'id de ligne de
// détail (injecté par les tests).
export function champsMetier(f, { faireId = faireIdLigne } = {}) {
  return {
    client: {
      nom: coupe(f?.client?.nom, LIMITES.texteCourt),
      contactNom: coupe(f?.client?.contactNom, LIMITES.texteCourt),
      telephone: coupe(f?.client?.telephone, LIMITES.telephone),
      email: coupe(f?.client?.email, LIMITES.texteCourt),
    },
    numBonCommande: coupe(f?.numBonCommande, LIMITES.numBonCommande),
    lieu: {
      adresse: coupe(f?.lieu?.adresse, LIMITES.adresse),
      precision: coupe(f?.lieu?.precision, LIMITES.adresse),
    },
    contactSurPlace: {
      nom: coupe(f?.contactSurPlace?.nom, LIMITES.texteCourt),
      telephone: coupe(f?.contactSurPlace?.telephone, LIMITES.telephone),
    },
    travaux: coupe(f?.travaux, LIMITES.travaux),
    description: str(f?.description).trim().slice(0, LIMITES.description),
    detail: normaliserDetail(f?.detail, faireId),
    urgence: URGENCES.includes(f?.urgence) ? f.urgence : "normale",
    tempsEstime: normaliserTempsEstime(f?.tempsEstime),
    responsable: { id: str(f?.responsable?.id), uid: str(f?.responsable?.uid), nom: coupe(f?.responsable?.nom, LIMITES.texteCourt) },
  };
}

// Clés EXACTES du document (§3.1). La règle fait un keys().hasOnly : aucune
// autre clé ne doit partir à la création.
export const CLES_DOCUMENT = Object.freeze([
  "numIntervention", "origine", "client", "numBonCommande", "lieu", "contactSurPlace",
  "travaux", "description", "detail", "urgence", "tempsEstime", "responsable",
  "intervenantIds", "intervenantUids", "intervenantNoms", "accesUids", "accesIds",
  "statut", "realisation", "source", "ia", "creePar", "creeParNom", "dateCreation", "majAt",
  "modePlanif", "passages", "bon", "factureePar", "factureeLe", "ouverte",
  "annuleePar", "annuleeLe", "motifAnnulation",
]);

// Document de création. `horodatage` = serverTimestamp() injecté (pur).
export function construireIntervention(f, { numIntervention, origine, source, ia, auteur, horodatage, faireId }) {
  const doc = {
    numIntervention,
    origine: ORIGINES.includes(origine) ? origine : "saisie",
    ...champsMetier(f, faireId ? { faireId } : undefined),
    intervenantIds: [], intervenantUids: [], intervenantNoms: [],
    accesUids: [], accesIds: [],
    statut: "a_planifier",
    realisation: null,
    source: source ? {
      nom: str(source.nom), path: str(source.path), url: str(source.url),
      contentType: str(source.contentType), tailleKo: Math.round(Number(source.tailleKo) || 0),
    } : null,
    ia: ia ? { modele: str(ia.modele), at: str(ia.at), champsIncertains: (ia.champsIncertains || []).map(str) } : null,
    creePar: str(auteur?.id),
    creeParNom: str(auteur?.nom),
    dateCreation: horodatage,
    majAt: horodatage,
    modePlanif: "bloc",
    passages: [],
    bon: null,
    factureePar: null,
    factureeLe: null,
    ouverte: true,
    annuleePar: null,
    annuleeLe: null,
    motifAnnulation: "",
  };
  return avecAcces(doc);
}

// Patch de « Modifier » : champs métier + mode de planification ramené à
// « bloc » si le détail a été vidé (« lignes » sans détail est refusé par
// la règle). Les accès sont recalculés par modifierIntervention.
export function patchEdition(intervention, f, opts) {
  const patch = champsMetier(f, opts);
  if (intervention?.modePlanif === "lignes" && !modePlanifValide("lignes", patch.detail)) patch.modePlanif = "bloc";
  return patch;
}

// ─── Numérotation INT-AAAA-NNNN (calque creationReserves) ──────
export function champCompteur(annee) { return `interventions_${annee}`; }
export function formatNumero(annee, seq) { return `INT-${annee}-${String(seq).padStart(4, "0")}`; }
export function prochainNumero(compteurs, annee) {
  const seq = (Number(compteurs?.[champCompteur(annee)]) || 0) + 1;
  return { seq, numero: formatNumero(annee, seq), champ: champCompteur(annee) };
}

// ─── Transitions (§3.2) ────────────────────────────────────────
export const TRANSITIONS = Object.freeze({
  a_planifier: ["planifiee", "annulee"],
  planifiee: ["a_planifier", "realisee", "annulee"],
  realisee: ["facturee", "annulee"],
  facturee: ["realisee"],
  annulee: [],
});
export function transitionPermise(de, vers) {
  return Object.prototype.hasOwnProperty.call(TRANSITIONS, de) && TRANSITIONS[de].includes(vers);
}
export function aAcces(intervention, uid) {
  return !!uid && (intervention?.accesUids || []).includes(uid);
}

// ─── Prédicats d'écran — MIROIRS des règles `interventions` ────
// allow create : estRolePlanning()
export function peutCreerIntervention(role) { return peutPlanifier(role); }
// allow update (branche bureau) : estRolePlanning()
export function peutModifierIntervention(role) { return peutPlanifier(role); }
// allow delete : isDirection()
export function peutSupprimerIntervention(role) { return ROLES_SUPPRESSION.includes(role); }

// Transition `vers` permise pour CE rôle sur CETTE fiche.
//  • realisee depuis planifiee : rôle planning, ou tout employé présent dans
//    accesUids (branche terrain de la règle) — ET la clôture porte un bon
//    d'intervention (`bon` non null) et une réalisation VALIDE (signée, ou
//    client absent avec commentaire). `patch` = ce qui va être écrit ; sans
//    patch, on juge la fiche telle qu'elle est ;
//  • facturee, et facturee → realisee : Admin / Direction / Assistante ;
//  • le reste : rôles planning.
export function peutTransition({ role, uid, intervention, vers, patch }) {
  const de = intervention?.statut;
  if (!transitionPermise(de, vers)) return false;
  if (vers === "facturee" || de === "facturee") return ROLES_FACTURATION.includes(role);
  if (de === "planifiee" && vers === "realisee") {
    const cible = patch || intervention;
    return peutCloturer({ role, uid, intervention }) && !!cible?.bon && realisationValide(cible?.realisation);
  }
  return peutPlanifier(role);
}

// Ouvrir le « Bon d'intervention » (clôture) : fiche planifiée, rôle
// planning ou personne présente dans accesUids. Le contenu (bon +
// réalisation valide) est contrôlé par peutTransition avec le patch.
export function peutCloturer({ role, uid, intervention }) {
  return intervention?.statut === "planifiee" && (peutPlanifier(role) || aAcces(intervention, uid));
}

// ─── Passages (§3.7.4) ─────────────────────────────────────────
// Un passage = une visite sur place, saisie par la personne connectée.
// Écriture = ancien tableau + UN passage (jamais réécrit autrement) : la
// règle exige `hasAll` sur l'ancien et une taille + 1.
export function peutAjouterPassage({ role, uid, intervention }) {
  return intervention?.statut === "planifiee"
    && (intervention?.passages || []).length < LIMITES.passages
    && (peutPlanifier(role) || aAcces(intervention, uid));
}
export function heuresValides(h, max) {
  const n = Number(h);
  return Number.isFinite(n) && n >= 0.5 && n <= max && Math.round(n * 2) === n * 2;
}
export function validerPassage(p) {
  const e = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str(p?.date))) e.date = "Date obligatoire.";
  if (!heuresValides(p?.heures, LIMITES.heuresPassage)) e.heures = `Entre 0,5 et ${LIMITES.heuresPassage} h, par pas de 0,5.`;
  if (str(p?.commentaire).length > LIMITES.commentairePassage) e.commentaire = `${LIMITES.commentairePassage} caractères maximum.`;
  return e;
}
// → { id, date, intervenantId, intervenantNom, heures, commentaire, at }
export function construirePassage(p, { auteur, id, at }) {
  return {
    id: str(id),
    date: str(p?.date),
    intervenantId: str(auteur?.id),
    intervenantNom: coupe(auteur?.nom, LIMITES.texteCourt),
    heures: Math.round(Number(p?.heures) * 2) / 2,
    commentaire: str(p?.commentaire).trim().slice(0, LIMITES.commentairePassage),
    at: str(at),
  };
}
// Ancien tableau + le passage, à la fin. Ne mute rien. Plafond atteint →
// erreur (la règle refuserait de toute façon).
export function ajouterPassage(passages, passage) {
  const l = Array.isArray(passages) ? passages : [];
  if (l.length >= LIMITES.passages) throw new Error(`${LIMITES.passages} passages maximum.`);
  return [...l, passage];
}
export function totalHeuresPassages(passages) {
  const t = (Array.isArray(passages) ? passages : []).reduce((s, p) => s + (Number(p?.heures) || 0), 0);
  return Math.round(t * 2) / 2;
}
// Temps estimé ramené en heures (une demi-journée ≈ 4 h) — affichage seul.
export const HEURES_PAR_DEMI = 4;
export function heuresEstimees(t) {
  const n = normaliserTempsEstime(t);
  return n.unite === "demi" ? n.valeur * HEURES_PAR_DEMI : n.valeur;
}
export function libelleHeures(h) {
  return `${String(Math.round(Number(h || 0) * 2) / 2).replace(".", ",")} h`;
}
// Total prérempli du bon : somme des passages ; sans passage, le temps
// estimé (en heures, plafonné à la limite).
export function heuresPrerempliesBon(intervention) {
  const t = totalHeuresPassages(intervention?.passages);
  if (t >= 0.5) return Math.min(t, LIMITES.heuresPassees);
  return Math.min(Math.max(0.5, heuresEstimees(intervention?.tempsEstime)), LIMITES.heuresPassees);
}

// ─── Réalisation (bon d'intervention, §3.7.5) ──────────────────
// Saisie de l'écran : { date, heuresPassees, commentaire, photos[],
// clientAbsent, signataireNom, signataireQualite, signatureFaite }.
// `signatureFaite` = une signature est dessinée (l'image n'est déposée
// qu'à l'envoi).
export function validerRealisation(r) {
  const e = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str(r?.date))) e.date = "Date obligatoire.";
  if (!heuresValides(r?.heuresPassees, LIMITES.heuresPassees)) e.heuresPassees = `Entre 0,5 et ${LIMITES.heuresPassees} h, par pas de 0,5.`;
  if (str(r?.commentaire).length > LIMITES.commentaireRealisation) e.commentaire = `${LIMITES.commentaireRealisation} caractères maximum.`;
  if ((r?.photos || []).length > LIMITES.photosRealisation) e.photos = `${LIMITES.photosRealisation} photos maximum.`;
  if (str(r?.signataireNom).trim().length > LIMITES.signataireNom) e.signataireNom = `${LIMITES.signataireNom} caractères maximum.`;
  if (str(r?.signataireQualite).trim().length > LIMITES.signataireQualite) e.signataireQualite = `${LIMITES.signataireQualite} caractères maximum.`;
  if (r?.clientAbsent === true) {
    if (!str(r?.commentaire).trim()) e.commentaire = "Client absent : le commentaire est obligatoire.";
  } else {
    if (!str(r?.signataireNom).trim()) e.signataireNom = "Nom du signataire obligatoire.";
    if (!r?.signatureFaite) e.signature = "Faites signer le client, ou cochez « Client absent ».";
  }
  return e;
}
export function estDataUrl(v) {
  return /^data:/i.test(str(v).trim());
}
// Document `realisation` (§3.1) — 11 clés, toujours toutes. Client absent :
// signataire et signature vides. `signaturePng` = URL Storage, JAMAIS une
// dataURL (refusée ici, refusée par la règle).
export function construireRealisation(r, { auteur, at, signaturePng = "" } = {}) {
  if (estDataUrl(signaturePng)) throw new Error("Signature : une URL de fichier est attendue, pas une image en ligne.");
  const absent = r?.clientAbsent === true;
  return {
    date: str(r?.date),
    heuresPassees: Math.round(Number(r?.heuresPassees) * 2) / 2,
    commentaire: str(r?.commentaire).trim().slice(0, LIMITES.commentaireRealisation),
    photos: (r?.photos || []).slice(0, LIMITES.photosRealisation).map((p) => ({ url: str(p?.url), path: str(p?.path) })),
    signataireNom: absent ? "" : coupe(r?.signataireNom, LIMITES.signataireNom),
    signataireQualite: absent ? "" : coupe(r?.signataireQualite, LIMITES.signataireQualite),
    signaturePng: absent ? "" : str(signaturePng).trim(),
    clientAbsent: absent,
    parId: str(auteur?.id),
    parNom: str(auteur?.nom),
    at: str(at),
  };
}
// Miroir de la règle : signée (nom + URL de signature) OU client absent
// avec commentaire. Jamais de dataURL.
export function realisationValide(r) {
  if (!r || typeof r !== "object") return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str(r.date)) || !heuresValides(r.heuresPassees, LIMITES.heuresPassees)) return false;
  if (estDataUrl(r.signaturePng)) return false;
  if (r.clientAbsent === true) return !!str(r.commentaire).trim();
  if (r.clientAbsent !== false) return false;
  return !!str(r.signataireNom).trim() && !!str(r.signaturePng).trim();
}

// ─── Facturation (§3.7.9) ──────────────────────────────────────
// realisee → facturee pose factureePar {id, nom} + factureeLe ;
// facturee → realisee les remet à null. `horodatage` = serverTimestamp().
export function patchFacturation({ vers, auteur, horodatage }) {
  if (vers === "facturee") {
    return { statut: "facturee", factureePar: { id: str(auteur?.id), nom: str(auteur?.nom) }, factureeLe: horodatage };
  }
  if (vers === "realisee") return { statut: "realisee", factureePar: null, factureeLe: null };
  throw new Error(`patchFacturation : statut ${vers} hors facturation`);
}

// ─── Annulation tracée ─────────────────────────────────────────
// Passer à « annulee » pose QUI (annuleePar {id, nom}), QUAND (annuleeLe =
// serverTimestamp(), la règle exige request.time) et POURQUOI
// (motifAnnulation, trimé, 3..500). Interdit depuis facturee ou annulee
// (miroir de la règle). Champs figés ensuite.
export const STATUTS_NON_ANNULABLES = Object.freeze(["facturee", "annulee"]);
export const RAPPEL_ANNULATION_PLANNING = "Les créneaux déjà au planning restent à retirer.";
export function validerMotifAnnulation(motif) {
  const m = str(motif).trim();
  if (m.length < LIMITES.motifAnnulationMin) return `Motif obligatoire (${LIMITES.motifAnnulationMin} caractères minimum).`;
  if (m.length > LIMITES.motifAnnulation) return `${LIMITES.motifAnnulation} caractères maximum.`;
  return "";
}
export function patchAnnulation({ statutActuel, auteur, motif, horodatage }) {
  if (STATUTS_NON_ANNULABLES.includes(statutActuel)) {
    throw new Error(statutActuel === "annulee" ? "Cette intervention est déjà annulée." : "Une intervention facturée ne s'annule pas.");
  }
  const erreur = validerMotifAnnulation(motif);
  if (erreur) throw new Error(erreur);
  const id = str(auteur?.id).trim();
  if (!id) throw new Error("Auteur de l'annulation inconnu.");
  return {
    statut: "annulee",
    annuleePar: { id, nom: str(auteur?.nom).slice(0, LIMITES.texteCourt) },
    annuleeLe: horodatage,
    motifAnnulation: str(motif).trim(),
  };
}
// Bandeau d'une fiche annulée. `dateTexte` = annuleeLe déjà formatée par
// l'écran. Fiche ancienne (sans trace) : « Annulée ».
export function libelleAnnulation(intervention, dateTexte = "") {
  if (intervention?.statut !== "annulee") return "";
  const nom = str(intervention?.annuleePar?.nom).trim();
  const motif = str(intervention?.motifAnnulation).trim();
  if (!nom && !motif && !dateTexte) return "Annulée";
  let t = "Annulée";
  if (dateTexte) t += ` le ${dateTexte}`;
  if (nom) t += ` par ${nom}`;
  if (motif) t += ` — Motif : ${motif}`;
  return t;
}

// ─── Dates ─────────────────────────────────────────────────────
// Timestamp Firestore, Date, ISO ou millisecondes → millisecondes (ou null).
export function enMillis(v) {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "string") { const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
  return null;
}

// ─── États affichés (§3.3) — calculés, jamais stockés ──────────
const JOUR_MS = 24 * 3600 * 1000;
// enRetard : non planifiée depuis plus d'1 j (urgente) ou de 2 j (normale).
export function estEnRetard(intervention, maintenant = Date.now()) {
  if (intervention?.statut !== "a_planifier") return false;
  const cree = enMillis(intervention?.dateCreation);
  if (cree == null) return false;
  const delai = intervention?.urgence === "urgente" ? JOUR_MS : 2 * JOUR_MS;
  return enMillis(maintenant) - cree > delai;
}
// aReplanifier : planifiée, mais aucun créneau ne la porte. `nbCreneaux`
// null = inconnu (lecture non faite ou en échec) → jamais affiché à tort.
export function estAReplanifier(intervention, nbCreneaux) {
  return intervention?.statut === "planifiee" && nbCreneaux === 0;
}

// Étiquette terrain : quel lien me donne accès à cette fiche.
export function etiquetteAcces(intervention, uid) {
  if (!uid) return "";
  if (intervention?.responsable?.uid === uid) return "Responsable";
  if ((intervention?.intervenantUids || []).includes(uid)) return "Intervenant";
  return "";
}

// ─── Notifications d'accueil ───────────────────────────────────
// Lues sur le MÊME périmètre que la liste (listerInterventions).
//  • à planifier : fiches OUVERTES, pour tout rôle ayant le module (§2) ;
//  • réalisées à facturer : rôles facturation ;
//  • terrain : planifiées où j'ai accès (à venir).
// `unique` = la seule fiche concernée, pour que la bannière la nomme et y
// mène.
export function notificationsInterventions({ interventions, role, uid }) {
  const liste = Array.isArray(interventions) ? interventions : [];
  const paquet = (items) => (items.length ? { count: items.length, unique: items.length === 1 ? items[0] : null } : null);
  return {
    aPlanifier: paquet(liste.filter((i) => estOuverte(i))),
    aVenir: estTerrain(role) ? paquet(liste.filter((i) => i.statut === "planifiee" && aAcces(i, uid))) : null,
    aFacturer: ROLES_FACTURATION.includes(role) ? paquet(liste.filter((i) => i.statut === "realisee")) : null,
  };
}

// ─── Onglets de la liste (§2) ──────────────────────────────────
// « À planifier » (fiches ouvertes, tout le monde), « Mes interventions »
// (responsable ou intervenant), « Toutes » (superviseurs).
export const ONGLET_A_PLANIFIER = "a_planifier";
export const ONGLET_MES = "mes";
export const ONGLET_TOUTES = "toutes";
export const ONGLET_LABEL = {
  [ONGLET_A_PLANIFIER]: "À planifier",
  [ONGLET_MES]: "Mes interventions",
  [ONGLET_TOUTES]: "Toutes",
};
// « .v2 » (03/11) : les superviseurs ouvrent désormais sur « Toutes » ;
// changer la clé efface l'ancien choix mémorisé une fois.
export const CLE_ONGLET = "epj.interventions.onglet.v2";
export function ongletsDisponibles(role) {
  return estSuperviseur(role) ? [ONGLET_A_PLANIFIER, ONGLET_MES, ONGLET_TOUTES] : [ONGLET_A_PLANIFIER, ONGLET_MES];
}
export function estMienne(intervention, uid) {
  return !!uid && (intervention?.responsable?.uid === uid || (intervention?.intervenantUids || []).includes(uid));
}
export function passeOnglet(intervention, onglet, uid) {
  if (onglet === ONGLET_A_PLANIFIER) return estOuverte(intervention);
  if (onglet === ONGLET_MES) return estMienne(intervention, uid);
  return onglet === ONGLET_TOUTES;
}
// → { a_planifier: n, mes: n, toutes?: n }
export function compterOnglets(liste, { role, uid } = {}) {
  const out = {};
  for (const o of ongletsDisponibles(role)) out[o] = (liste || []).filter((i) => passeOnglet(i, o, uid)).length;
  return out;
}
// Onglet affiché : le mémorisé s'il existe pour ce rôle ; sinon « À
// planifier » s'il est non vide, sinon « Mes interventions ».
export function ongletParDefaut({ memorise, role, uid, interventions }) {
  const dispo = ongletsDisponibles(role);
  if (dispo.includes(memorise)) return memorise;
  if (estSuperviseur(role)) return ONGLET_TOUTES;
  return (interventions || []).some((i) => estOuverte(i)) ? ONGLET_A_PLANIFIER : ONGLET_MES;
}
// Stockage injecté (jamais `localStorage` en dur : il peut jeter).
export function lireOnglet(storage) {
  try { return (storage && storage.getItem(CLE_ONGLET)) || null; } catch { return null; }
}
export function memoriserOnglet(storage, onglet) {
  try { if (storage) storage.setItem(CLE_ONGLET, onglet); } catch { /* préférence perdue, sans gravité */ }
}

// Âge d'une fiche (jours entiers depuis la création) et libellé « depuis 3 j ».
export function ageJours(intervention, maintenant = Date.now()) {
  const cree = enMillis(intervention?.dateCreation);
  if (cree == null) return null;
  return Math.max(0, Math.floor((enMillis(maintenant) - cree) / JOUR_MS));
}
export function libelleAge(intervention, maintenant = Date.now()) {
  const n = ageJours(intervention, maintenant);
  if (n == null) return "";
  return n === 0 ? "depuis aujourd'hui" : `depuis ${n} j`;
}

// ─── Agenda (§4) : contenu d'un événement pour UNE ligne de créneau ──
// titre « INT-… · client · travaux » ; lieu = adresse + précision ;
// détails = contact sur place + téléphone, n° de bon, lien vers la fiche.
export function evenementAgenda(intervention, origine = "") {
  const i = intervention || {};
  const titre = [i.numIntervention, i.client?.nom, i.travaux].map((v) => str(v).trim()).filter(Boolean).join(" · ");
  const lieu = [i.lieu?.adresse, i.lieu?.precision].map((v) => str(v).trim()).filter(Boolean).join(" — ");
  const contact = [i.contactSurPlace?.nom, i.contactSurPlace?.telephone].map((v) => str(v).trim()).filter(Boolean).join(" ");
  const details = [
    contact && `Contact sur place : ${contact}`,
    str(i.numBonCommande).trim() && `N° de bon : ${str(i.numBonCommande).trim()}`,
    i.id && `${str(origine).replace(/\/+$/, "")}/#/interventions/${encodeURIComponent(i.id)}`,
  ].filter(Boolean).join("\n");
  return { titre, lieu, details };
}

// ─── Pré-remplissage IA (sortie de la callable extraireIntervention) ──
// Champs du formulaire repérables à l'écran. `champsIncertains` peut nommer
// le chemin complet (« client.telephone ») ou la seule feuille
// (« telephone ») : on accepte les deux.
export const CHAMPS_IA = Object.freeze([
  "client.nom", "client.contactNom", "client.telephone", "client.email",
  "numBonCommande", "lieu.adresse", "lieu.precision",
  "contactSurPlace.nom", "contactSurPlace.telephone",
  "travaux", "description", "urgence",
]);
function lireChemin(obj, chemin) {
  return chemin.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
// → { formulaire, remplis: [chemins], incertains: [chemins], modele }
export function appliquerExtraction(formulaire, sortie, faireId = faireIdLigne) {
  const f = JSON.parse(JSON.stringify(formulaire || formulaireVide()));
  const remplis = [];
  for (const chemin of CHAMPS_IA) {
    let v = lireChemin(sortie, chemin);
    if (chemin === "urgence") {
      if (!URGENCES.includes(v)) continue;
    } else {
      v = typeof v === "string" ? v.trim() : "";
      if (!v) continue;
    }
    const [a, b] = chemin.split(".");
    if (b) f[a] = { ...f[a], [b]: v };
    else f[a] = v;
    remplis.push(chemin);
  }
  // Détail des travaux (§3.5) : `detail[]` { libelle, demiJournees } SANS
  // id — chaque ligne reçoit ici son id stable. Vide → détail inchangé.
  const detail = normaliserDetail(Array.isArray(sortie?.detail) ? sortie.detail.map((d) => ({ ...d, id: "" })) : [], faireId);
  if (detail.length) { f.detail = detail; remplis.push("detail"); }
  const bruts = Array.isArray(sortie?.champsIncertains) ? sortie.champsIncertains.map(str) : [];
  const incertains = CHAMPS_IA.filter((c) => bruts.includes(c) || bruts.includes(c.split(".").pop()));
  // « detail » (posé par la callable quand une durée manquait) : le bloc
  // « Détailler les travaux » est encadré et déplié (etatBlocDetail).
  if (bruts.includes("detail")) incertains.push("detail");
  return { formulaire: f, remplis, incertains, modele: str(sortie?.modele) };
}

// Bloc « Détailler les travaux » du formulaire : « detail » incertain (IA)
// → encadré warning ET déplié ; sinon déplié seulement s'il a des lignes.
export function etatBlocDetail({ detail, incertains } = {}) {
  const alerte = Array.isArray(incertains) && incertains.includes("detail");
  return { alerte, ouvert: alerte || (Array.isArray(detail) && detail.length > 0) };
}

// ─── Dépôt : types acceptés ────────────────────────────────────
export const MAX_DEPOT_MO = 20;
export const MESSAGE_MSG = "Enregistrez le mail en PDF ou copiez son texte";
// La callable refuse une image de plus de 3,7 Mo (limite base64 de l'API) :
// toute image est ré-encodée en JPEG dans le navigateur avant dépôt
// (`image: true`), puis contrôlée contre ce plafond.
export const MAX_IMAGE_OCTETS = Math.floor(3.7 * 1024 * 1024);
export const MESSAGE_IMAGE_LOURDE = "Image trop lourde même réduite (3,7 Mo maximum) : envoyez-la en PDF ou faites une capture plus petite.";
// → { ok, contentType, image, erreur }
export function controlerDepot({ nom, type, taille }) {
  const n = str(nom).toLowerCase();
  if (n.endsWith(".msg")) return { ok: false, erreur: MESSAGE_MSG };
  if ((Number(taille) || 0) > MAX_DEPOT_MO * 1024 * 1024) return { ok: false, erreur: `Fichier trop lourd (${MAX_DEPOT_MO} Mo maximum).` };
  if (n.endsWith(".pdf") || type === "application/pdf") return { ok: true, contentType: "application/pdf" };
  if (/^image\//.test(str(type)) || /\.(jpe?g|png|webp|gif|heic|heif)$/.test(n)) return { ok: true, contentType: "image/jpeg", image: true };
  if (n.endsWith(".eml") || type === "message/rfc822") return { ok: true, contentType: "message/rfc822" };
  if (n.endsWith(".txt") || type === "text/plain") return { ok: true, contentType: "text/plain" };
  return { ok: false, erreur: "Format non pris en charge : PDF, image, .eml ou .txt." };
}
// Nom de fichier assaini pour Storage : accents retirés, [a-z0-9._-] seuls.
export function nomAssaini(nom) {
  const s = str(nom).normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_").replace(/_+/g, "_").replace(/^[_.]+/, "");
  return (s || "document").slice(-80);
}
export function cheminDepot(uid, ts, nom) {
  if (!uid) throw new Error("cheminDepot : uid manquant");
  return `interventionsDepots/${uid}/${ts}_${nomAssaini(nom)}`;
}
export function cheminPhotoRealisation(interventionId, ts, nom) {
  if (!interventionId) throw new Error("cheminPhotoRealisation : id manquant");
  return `interventions/${interventionId}/${ts}_${nomAssaini(nom)}`;
}
